import { ChangeEvent, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Paper from "@material-ui/core/Paper";
import Divider from "@material-ui/core/Divider";
import { schema } from "./schema";
import { useFormStyles } from "./styles";
import { UseMutationResult } from "@tanstack/react-query";
import TextField from "@material-ui/core/TextField";
import WatchLater from "@material-ui/icons/WatchLater";
import ServiceField from "./components/service-field";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import Typography from "@material-ui/core/Typography";
import InputMask from "react-input-mask";
import { AttendanceLocalDepartmentQueue } from "../../../../../../../../../../../../../@types/attendance-local-department-queue";
import { AttendanceLocalDepartmentQueueServiceTimeComplementException } from "../../../../../../../../../../../../../@types/attendance-local-department-queue-service-time-complement-exception";
import { AttendanceLocalDepartmentQueueServiceTimeComplementExceptionCreateData } from "../../../../../../../../../../../../../services/api-tansquery/attendance-local/department/queue/service-time/complement/exception/create";
import CalendarToday from "@material-ui/icons/CalendarToday";
import Grid from "@material-ui/core/Grid";
import { AttendanceLocal } from "../../../../../../../../../../../../../@types/attendance-local";
import { AttendanceLocalDepartment } from "../../../../../../../../../../../../../@types/attendance-local-department";
import { handleNumberChange } from "../../../../../../../../../../../../../utils/handleNumberChange";

type FormProps = {
    unidade: AttendanceLocal["slug_unidade"];
    departmentId: AttendanceLocalDepartment["id"];
    queueId: AttendanceLocalDepartmentQueue["id"];
    serviceTimeComplementException?: AttendanceLocalDepartmentQueueServiceTimeComplementException;
    mutation?: UseMutationResult<
        AttendanceLocalDepartmentQueueServiceTimeComplementException,
        unknown,
        AttendanceLocalDepartmentQueueServiceTimeComplementExceptionCreateData,
        unknown
    >
};

export default function Form({ unidade, departmentId, queueId, serviceTimeComplementException, mutation }: FormProps) {
    const numberFieldProps = { min: 0, max: 100 };
    const history = useHistory();
    const { classes } = useFormStyles();
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            setor: departmentId.toString(),
            fila: queueId.toString(),
            servico: serviceTimeComplementException?.servico.toString() || "",
            tempo: "",
            data: "",
            horario_especial: false,
            hora_inicio: "",
            hora_fim: "",
            intervalo_hora_inicio: "",
            intervalo_hora_fim: "",
        }
    });

    useEffect(() => {
        if (serviceTimeComplementException) {
            form.reset({
                setor: departmentId.toString(),
                fila: queueId.toString(),
                servico: serviceTimeComplementException.servico.id.toString(),
                tempo: serviceTimeComplementException.tempo?.toString() || "",
                data: serviceTimeComplementException.data.split("-").reverse().join("/") || "",
                horario_especial: serviceTimeComplementException.horario_especial || false,
                hora_inicio: serviceTimeComplementException.hora_inicio || "",
                hora_fim: serviceTimeComplementException.hora_fim || "",
                intervalo_hora_inicio: serviceTimeComplementException.intervalo_hora_inicio || "",
                intervalo_hora_fim: serviceTimeComplementException.intervalo_hora_fim || "",
            });
        }
    }, [form, serviceTimeComplementException, queueId, departmentId]);

    async function handleSubmit(data: z.infer<typeof schema>) {
        if (mutation.isLoading) {
            return;
        }

        await mutation.mutateAsync({
            setor: departmentId,
            fila: queueId,
            servico: Number(data.servico),
            tempo: Number(data.tempo),
            data: data.data.split("/").reverse().join("-"),
            horario_especial: data.horario_especial,
            hora_inicio: data.hora_inicio || null,
            hora_fim: data.hora_fim || null,
            intervalo_hora_inicio: data.intervalo_hora_inicio || null,
            intervalo_hora_fim: data.intervalo_hora_fim || null,
        })
    }

    function preventNonNumericInput(event: React.KeyboardEvent<HTMLInputElement>) {
        if (!/[0-9]/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Delete' && event.key !== 'Tab' && event.key !== 'Enter') {
            event.preventDefault();
        }
    }

    useEffect(() => {
        if (mutation.isSuccess) {
            history.goBack();
        }
    }, [mutation.isSuccess, history]);

    return (
        <Paper
            elevation={3}
            style={{ padding: 20 }}
            component="form"
            onSubmit={form.handleSubmit(handleSubmit)}
        >
            <Grid container spacing={2}>
                <Grid item xs={12} component={Typography} className={classes.title}>
                    Gerenciar Exceção de Horários (Serviço Complementar)
                </Grid>

                <Grid item xs={12}>
                    <Controller
                        name="servico"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <ServiceField
                                field={field}
                                fieldState={fieldState}
                                unidade={unidade}
                                setor_unidade={departmentId}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Controller
                        name="data"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <InputMask
                                mask="99/99/9999"
                                maskChar={null}
                                value={field.value || ""}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    field.onChange(e.target.value);
                                }}
                            >
                                {(inputProps: any) => (
                                    <TextField
                                        {...inputProps}
                                        required
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        label="Data da Exceção"
                                        inputProps={{ className: classes.textField }}
                                        error={!!fieldState.error}
                                        helperText={
                                            fieldState.error ? fieldState.error.message : ""
                                        }
                                        InputProps={{
                                            required: false,
                                            endAdornment: <CalendarToday htmlColor="#C4C4C4" />
                                        }}
                                    />
                                )}
                            </InputMask>
                        )}
                    />
                </Grid>

                <Divider style={{ margin: "20px 0" }} />

                <Grid item xs={12}>
                    <Controller
                        name="tempo"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                required
                                fullWidth
                                type="number"
                                margin="normal"
                                variant="outlined"
                                label="Tempo de Atendimento Presencial"
                                error={!!fieldState.error}
                                helperText={
                                    fieldState.error ? fieldState.error.message : "Informe o tempo de atendimento presencial em minutos"
                                }
                                inputProps={{
                                    ...numberFieldProps,
                                    className: classes.textField,
                                }}
                                InputProps={{
                                    required: false,
                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                }}
                                onKeyPress={preventNonNumericInput}
                                onChange={(e) => {
                                    let value = handleNumberChange(
                                        numberFieldProps.min,
                                        numberFieldProps.max,
                                        e.target.value
                                    );
                                    field.onChange(value.toString());
                                }}
                            />
                        )}
                    />
                </Grid>

                <Divider style={{ margin: "20px 0" }} />

                <Grid item xs={12}>
                    <Controller
                        name="horario_especial"
                        control={form.control}
                        render={({ field }) => (
                            <FormControl>
                                <FormControlLabel
                                    label="Horário de Agendamento Especial?"
                                    control={<Switch {...field} checked={field.value} />}
                                />
                                <FormHelperText>
                                    Deseja configurar horários especiais para atendimentos agendados?
                                </FormHelperText>
                            </FormControl>
                        )}
                    />
                </Grid>

                {form.watch("horario_especial") && (
                    <>
                        <Grid item xs={6}>
                            <Controller
                                name="hora_inicio"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Início do horário de agendamento"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ?
                                                        fieldState.error.message
                                                        : "Informe o horário de início dos agendamentos"
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Controller
                                name="hora_fim"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Fim do horário de agendamento"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ?
                                                        fieldState.error.message
                                                        : "Informe o horário de finalização dos agendamentos"
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Controller
                                name="intervalo_hora_inicio"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Início do intervalo de agendamento"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ?
                                                        fieldState.error.message
                                                        : "Informe o horário de início do intervalo para pausa dos agendamentos"
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Controller
                                name="intervalo_hora_fim"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Fim do intervalo de agendamento"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ?
                                                        fieldState.error.message
                                                        : "Informe o horário de finalização do intervalo para pausa dos agendamentos"
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>
                    </>
                )}
            </Grid>

            <Box className={classes.footer}>
                <Button
                    className={classes.button}
                    onClick={() => history.goBack()}
                    disabled={mutation.isLoading}
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    className={classes.saveButton}
                    disabled={mutation.isLoading}
                >
                    {mutation.isLoading ? 'Salvando...' : 'Salvar'}
                </Button>
            </Box>
        </Paper>
    );
}
